import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Shield, 
  Users, 
  FileText, 
  BarChart3, 
  Clock, 
  CheckCircle, 
  ArrowRight,
  Zap,
  Globe,
  Download,
  PieChart,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import Navbar from '../../../components/layout/Navbar';
import Footer from '../../../components/layout/Footer';
import { fetchPublicAppSettings } from '../../../services/superadmin_services/settings_services';
import { APP_NAME } from '../../../constants/appConstants';

export default function WelcomePage() {
  const [typedText, setTypedText] = React.useState('');
  const [contactInfo, setContactInfo] = React.useState({
    contact_email: 'callcenter@ngombe.org',
    contact_phone: '+237 652 751 999',
    contact_address: 'Cameroon'
  });
  const fullText = 'Financial Future';
  
  // Load contact information from backend
  React.useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const response = await fetchPublicAppSettings();
        if (response.success) {
          setContactInfo({
            contact_email: response.data.contact_email,
            contact_phone: response.data.contact_phone,
            contact_address: response.data.contact_address
          });
        }
      } catch (error) {
        console.error('Error loading contact info:', error);
        // Keep default values on error
      }
    };

    loadContactInfo();
  }, []);
  
  React.useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100); // Speed of typing (100ms per character)
    
    return () => clearInterval(typingInterval);
  }, []);

  const features = [
    {
      icon: <FileText className="h-8 w-8" />,
      title: "Comprehensive Report Management",
      description: "Create, track, and manage expenses, revenues, bills, and proforma invoices all in one place with intuitive interfaces."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Team Collaboration",
      description: "Invite team members and collaborators with customizable permission levels. Share reports securely and work together seamlessly."
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Real-Time Analytics",
      description: "Get instant insights into your financial health with interactive dashboards, charts, and detailed activity tracking."
    },
    {
      icon: <Download className="h-8 w-8" />,
      title: "Export & Download",
      description: "Generate professional PDF reports and export data to Excel for further analysis or sharing with stakeholders."
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Secure & Private",
      description: "Your financial data is protected with secure authentication and data encryption to keep your information safe."
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Save Time",
      description: "Streamline your financial workflows and automate repetitive tasks to focus on what matters most for your business."
    }
  ];

  const benefits = [
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Lightning Fast",
      description: "Optimized performance for quick data entry and instant report generation"
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Access Anywhere",
      description: "Cloud-based platform accessible from any device, anytime"
    },
    {
      icon: <PieChart className="h-6 w-6" />,
      title: "Visual Insights",
      description: "Beautiful charts and graphs to understand your finances at a glance"
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Create Your Account",
      description: "Sign up in seconds with just your email and password. No credit card required."
    },
    {
      number: "02",
      title: "Set Up Your Workspace",
      description: "Customize categories, invite team members, and configure your preferences."
    },
    {
      number: "03",
      title: "Start Tracking",
      description: "Begin creating reports, tracking expenses, and managing your finances effortlessly."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 bg-secondary-100 rounded-full mb-6">
                <TrendingUp className="h-4 w-4 mr-2 text-secondary-600" />
                <span className="text-sm font-medium text-secondary-700">Smart Financial Management</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-gray-900">
                <span className="block animate-fade-in-up">Take Control of Your</span>
                <span className="block animate-fade-in-up animation-delay-200">
                  <span className="bg-gradient-to-r from-primary-600 via-secondary-600 to-primary-600 bg-clip-text text-transparent">
                    {typedText}
                  </span>
                  <span 
                    className="text-primary-600 inline-block ml-1 animate-blink"
                    style={{ 
                      animation: 'blink 1s ease-in-out infinite',
                      display: 'inline-block'
                    }}
                  >
                    |
                  </span>
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl animate-fade-in-up animation-delay-400">
                Streamline your financial operations with our comprehensive platform. Track expenses, manage revenues, collaborate with your team, and gain valuable insights—all in one place.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-600">
                <Link 
                  to="/register" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary-600 via-secondary-600 to-primary-600 bg-[length:200%_auto] hover:bg-right text-white font-semibold rounded-lg transform hover:scale-105 transition-all duration-500 shadow-lg hover:shadow-xl animate-gradient-shift"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link 
                  to="/login" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200 border-2 border-primary-600"
                >
                  Sign In
                </Link>
              </div>

              <div className="mt-8 flex items-center gap-6 justify-center lg:justify-start text-sm text-gray-600 animate-fade-in-up animation-delay-800">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-secondary-600 mr-2" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-secondary-600 mr-2" />
                  <span>Free to start</span>
                </div>
              </div>
            </div>

            {/* Right Content - Decorative Card */}
            <div className="hidden lg:block relative">
              <div className="relative bg-gradient-to-br from-secondary-600 to-secondary-800 rounded-3xl p-8 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-white/20">
                      <span className="text-white/80">Total Revenue</span>
                      <span className="text-2xl font-bold text-white">45,231,000 XAF</span>
                    </div>
                    <div className="flex items-center justify-between pb-4 border-b border-white/20">
                      <span className="text-white/80">Total Expenses</span>
                      <span className="text-2xl font-bold text-white">23,456,000 XAF</span>
                    </div>
                    <div className="flex items-center justify-between pb-4 border-b border-white/20">
                      <span className="text-white/80">Pending Bills</span>
                      <span className="text-2xl font-bold text-tertiary-300">8,920,000 XAF</span>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-white font-semibold">Net Profit</span>
                      <span className="text-3xl font-bold text-white">21,775,000 XAF</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-tertiary-500 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary-500 rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Your Finances
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features designed to simplify your financial management and help you make better business decisions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              // Alternate colors for feature cards
              let colorScheme = 'primary';
              if (index % 3 === 1) colorScheme = 'secondary';
              if (index % 3 === 2) colorScheme = 'tertiary';
              
              const gradientClass = 
                colorScheme === 'secondary' ? 'from-secondary-500 to-secondary-600' :
                colorScheme === 'tertiary' ? 'from-tertiary-500 to-tertiary-600' :
                'from-primary-500 to-primary-600';
              
              const hoverBorderClass = 
                colorScheme === 'secondary' ? 'hover:border-secondary-200' :
                colorScheme === 'tertiary' ? 'hover:border-tertiary-200' :
                'hover:border-primary-200';
              
              return (
                <div 
                  key={index}
                  className={`group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 ${hoverBorderClass}`}
                >
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${gradientClass} text-white rounded-xl mb-6 group-hover:scale-110 transition-transform`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose {APP_NAME}?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We've built a platform that combines powerful functionality with ease of use, giving you the tools you need without the complexity you don't.
              </p>

              <div className="space-y-6">
                {benefits.map((benefit, index) => {
                  // Use different colors for each benefit
                  const colorScheme = index === 0 ? 'secondary' : index === 1 ? 'tertiary' : 'primary';
                  const bgClass = 
                    colorScheme === 'secondary' ? 'bg-secondary-100' :
                    colorScheme === 'tertiary' ? 'bg-tertiary-100' :
                    'bg-primary-100';
                  const textClass = 
                    colorScheme === 'secondary' ? 'text-secondary-600' :
                    colorScheme === 'tertiary' ? 'text-tertiary-600' :
                    'text-primary-600';
                  
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-12 h-12 ${bgClass} rounded-lg flex items-center justify-center ${textClass} shadow-sm`}>
                        {benefit.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                <div className="space-y-6">
                  <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Total Revenue</span>
                    <span className="text-2xl font-bold text-secondary-600">45,231,000 XAF</span>
                  </div>
                  <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Total Expenses</span>
                    <span className="text-2xl font-bold text-red-600">23,456,000 XAF</span>
                  </div>
                  <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                    <span className="text-gray-600">Pending Bills</span>
                    <span className="text-2xl font-bold text-tertiary-600">8,920,000 XAF</span>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-gray-900 font-semibold">Net Profit</span>
                    <span className="text-3xl font-bold text-primary-600">21,775,000 XAF</span>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary-500 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary-500 rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, straightforward process to get you up and running
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Lines */}
            <div className="hidden md:block absolute top-1/4 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-primary-300 via-secondary-300 to-tertiary-300"></div>

            {steps.map((step, index) => {
              // Use different colors for each step
              const colorScheme = index === 0 ? 'primary' : index === 1 ? 'secondary' : 'tertiary';
              const gradientClass = 
                colorScheme === 'secondary' ? 'from-secondary-500 to-secondary-600' :
                colorScheme === 'tertiary' ? 'from-tertiary-500 to-tertiary-600' :
                'from-primary-500 to-primary-600';
              
              return (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-gray-100 relative z-10">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${gradientClass} text-white rounded-full text-2xl font-bold mb-6`}>
                      {step.number}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600">
              Have questions? We're here to help
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                <Mail className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Email Us</h3>
              <a href={`mailto:${contactInfo.contact_email}`} className="text-primary-600 hover:text-primary-700">
                {contactInfo.contact_email}
              </a>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary-100 rounded-full mb-4">
                <Phone className="h-8 w-8 text-secondary-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Call Us</h3>
              <a href={`tel:${contactInfo.contact_phone.replace(/\s/g, '')}`} className="text-primary-600 hover:text-primary-700">
                {contactInfo.contact_phone}
              </a>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-tertiary-100 rounded-full mb-4">
                <MapPin className="h-8 w-8 text-tertiary-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
              <p className="text-gray-600">
                {contactInfo.contact_address}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-600 via-secondary-700 to-primary-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Financial Management?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join businesses already using {APP_NAME} to streamline their finances and make smarter decisions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link 
              to="/login" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 shadow-lg border-2 border-white/30"
            >
              Sign In to Your Account
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-8 text-sm text-white/90">
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              <span>Setup in 5 minutes</span>
            </div>
            <div className="flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              <span>Secure platform</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span>No credit card needed</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
